import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { useAuth } from 'react-oidc-context';
import { useTranslation } from 'react-i18next';

// RequireAuth gates a subtree behind a successful Zitadel login. While the
// OIDC library determines auth state we render a small loading line; once
// resolved we either show the children or kick off the redirect to Zitadel.
//
// The redirect is fire-and-forget — once initiated, the browser navigates
// away and React unmounts. No additional state machine is needed.
export function RequireAuth({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const { t } = useTranslation('common');

  useEffect(() => {
    if (!auth.isLoading && !auth.isAuthenticated && !auth.activeNavigator) {
      void auth.signinRedirect();
    }
  }, [auth]);

  if (auth.isLoading || auth.activeNavigator) {
    return <p className="auth-status">{t('auth.loading')}</p>;
  }

  if (auth.error) {
    return (
      <div className="auth-status auth-status--error" role="alert">
        <p>{t('auth.error')}</p>
        <button type="button" onClick={() => void auth.signinRedirect()}>
          {t('auth.retry')}
        </button>
      </div>
    );
  }

  if (!auth.isAuthenticated) {
    return <p className="auth-status">{t('auth.redirecting')}</p>;
  }

  return <>{children}</>;
}
